import { trackComponent } from "@repo/analytics";
import { dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import { getPageType } from "@repo/shared-utils/common-utils";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";

/** Excel component / reference id — reused for sort on pages without a filter bar. */
export const STOCK_SORT_FILTER_BAR_CT = {
  componentName: "filter_bar_selection_chips_chevron",
  clickType: "filter_bar_selection_chips_chevron",
  referenceId: "filter_bar_selection_chips_chevron",
  productName: "trading_mall",
  productCategory: "stocks",
  propertyType: "plp",
  position: "filters,filter",
} as const;

/** Analytics context passed to sort chip when a page opts into the new-way CT event. */
export interface StockSortCtAnalytics {
  /** EP_SECTION — page heading (h2/h3) the sort control sits under. */
  sectionTitle: string;
}

interface TrackStockSortFilterBarCtInput {
  sectionTitle: string;
  ctaText: string;
  /** EP_SUB_SECTION — e.g. open, close, na, clear filters */
  subSection?: string;
  /** EP_POSITION — defaults to `filters,filter` for chevron; use `NA` for option select */
  position?: string;
}

/**
 * Fires the new-way (`trackComponent`) CleverTap event for stock-listing sort
 * interactions using the `filter_bar_selection_chips_chevron` spec (same event
 * name as the filter bar on top-losers / top-gainers).
 */
export async function trackStockSortFilterBarCt({
  sectionTitle,
  ctaText,
  subSection,
  position,
}: TrackStockSortFilterBarCtInput): Promise<void> {
  try {
    const customerId = await getCustomerIdForAnalytics();
    const eventName = getPageType("tag-8") || dlElementConstants.PLP_CLICKED;

    await trackComponent({
      eventName,
      componentName: STOCK_SORT_FILTER_BAR_CT.componentName,
      componentId: STOCK_SORT_FILTER_BAR_CT.referenceId,
      clickType: STOCK_SORT_FILTER_BAR_CT.clickType,
      eventType: dlEventConstants.CLICK,
      sectionTitle,
      ctaText,
      subSection,
      position,
      productName: STOCK_SORT_FILTER_BAR_CT.productName,
      productCategory: STOCK_SORT_FILTER_BAR_CT.productCategory,
      pageType: STOCK_SORT_FILTER_BAR_CT.propertyType,
      cleverTapOverrides: {
        EP_PROPERTY_TYPE: STOCK_SORT_FILTER_BAR_CT.propertyType,
        EP_PRODUCT_NAME: STOCK_SORT_FILTER_BAR_CT.productName,
        EP_PRODUCT_CATEGORY: STOCK_SORT_FILTER_BAR_CT.productCategory,
        EP_REFERENCE_ID: STOCK_SORT_FILTER_BAR_CT.referenceId,
        EP_CUST_ATTRIBUTE_1: "filters",
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        ...(subSection && { EP_SUB_SECTION: subSection }),
      },
    });
  } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to track stock sort filter bar click", error);
  }
}
